/**
 * ReadingFlow — Readers Panel UI
 * Stages: upload → reading (polling) → review → launch config → running → guidance → report
 */
import React, { useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload, FileText, Loader2, Users, Play, ChevronDown, ChevronUp,
  Trash2, RefreshCw, BookOpen, AlertTriangle, Check, X
} from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

// ─── Upload & Document Selection Stage ──────────────────────

export function ReadingUpload({ reading, library }) {
  const { t } = useTranslation();
  const [uploadMode, setUploadMode] = useState('pdf'); // 'pdf' | 'text'
  const [textInput, setTextInput] = useState('');
  const [titleInput, setTitleInput] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileDrop = useCallback(async (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file && file.type === 'application/pdf') {
      try {
        await reading.uploadDocument(file, null, titleInput || null);
      } catch (err) { /* error handled in hook */ }
    }
  }, [reading, titleInput]);

  const handleFileSelect = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        await reading.uploadDocument(file, null, titleInput || null);
      } catch (err) { /* error handled in hook */ }
    }
  }, [reading, titleInput]);

  const handleTextUpload = useCallback(async () => {
    if (!textInput.trim()) return;
    try {
      await reading.uploadDocument(null, textInput, titleInput || null);
      setTextInput('');
      setTitleInput('');
    } catch (err) { /* error handled in hook */ }
  }, [reading, textInput, titleInput]);

  const handleStartReading = () => {
    if (reading.selectedDocument && reading.selectedPanelId) {
      const docId = reading.selectedDocument.document_id || reading.selectedDocument.id;
      reading.startReading(docId, reading.selectedPanelId);
    }
  };

  const canStartReading = reading.selectedDocument && reading.selectedPanelId;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="font-serif text-3xl mb-2">{t('reading.title')}</h1>
        <p className="text-stone-500">{t('reading.subtitle')}</p>
      </div>

      {/* Upload Section */}
      <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
        <div className="flex items-center gap-2 mb-6">
          <Upload className="w-5 h-5 text-amber-500" />
          <h2 className="font-serif text-2xl">{t('reading.uploadTitle')}</h2>
        </div>

        {/* Upload mode toggle */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setUploadMode('pdf')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              uploadMode === 'pdf' ? 'bg-amber-100 text-amber-800' : 'bg-stone-100 text-stone-500'
            }`}
          >
            {t('reading.uploadPdf')}
          </button>
          <button
            onClick={() => setUploadMode('text')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              uploadMode === 'text' ? 'bg-amber-100 text-amber-800' : 'bg-stone-100 text-stone-500'
            }`}
          >
            {t('reading.uploadText')}
          </button>
        </div>

        {/* Optional title */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-stone-700 mb-1">{t('reading.titleLabel')}</label>
          <input
            type="text"
            value={titleInput}
            onChange={e => setTitleInput(e.target.value)}
            className="input w-full"
            placeholder={t('reading.titlePlaceholder')}
          />
        </div>

        {uploadMode === 'pdf' ? (
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleFileDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${
              dragOver ? 'border-amber-400 bg-amber-50' : 'border-stone-300 hover:border-amber-300 hover:bg-stone-50'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              className="hidden"
              onChange={handleFileSelect}
            />
            {reading.uploading ? (
              <Loader2 className="w-8 h-8 text-amber-500 animate-spin mx-auto mb-2" />
            ) : (
              <FileText className="w-8 h-8 text-stone-400 mx-auto mb-2" />
            )}
            <p className="text-stone-600 font-medium">
              {reading.uploading ? t('reading.uploading') : t('reading.dropZone')}
            </p>
            <p className="text-stone-400 text-sm mt-1">{t('reading.dropZoneHint')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            <textarea
              value={textInput}
              onChange={e => setTextInput(e.target.value)}
              className="input w-full h-48 resize-y"
              placeholder={t('reading.textPlaceholder')}
            />
            <button
              onClick={handleTextUpload}
              disabled={!textInput.trim() || reading.uploading}
              className="btn btn-primary flex items-center gap-2"
            >
              {reading.uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {t('reading.uploadBtn')}
            </button>
          </div>
        )}

        {reading.uploadError && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            <AlertTriangle className="w-4 h-4 inline mr-2" />
            {reading.uploadError}
          </div>
        )}
      </section>

      {/* Document List */}
      {reading.documents.length > 0 && (
        <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
          <h2 className="font-serif text-xl mb-4">{t('reading.documentsTitle')}</h2>
          <div className="space-y-3">
            {reading.documents.map(doc => {
              const isSelected = (reading.selectedDocument?.document_id || reading.selectedDocument?.id) === doc.id;
              return (
                <div
                  key={doc.id}
                  onClick={() => reading.selectDocument(doc.id)}
                  className={`flex items-center justify-between p-4 rounded-xl border cursor-pointer transition-all ${
                    isSelected
                      ? 'border-amber-400 bg-amber-50 shadow-sm'
                      : 'border-stone-200 hover:border-stone-300 hover:bg-stone-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <FileText className={`w-5 h-5 ${isSelected ? 'text-amber-600' : 'text-stone-400'}`} />
                    <div>
                      <p className="font-medium text-stone-800">{doc.title}</p>
                      <p className="text-sm text-stone-500">
                        {doc.source_type?.toUpperCase()} &middot; {doc.word_count?.toLocaleString()} {t('reading.words')}
                        {doc.page_count > 0 && ` · ${doc.page_count} ${t('reading.pages')}`}
                        {doc.has_extraction && <span className="ml-2 text-green-600">{t('reading.extracted')}</span>}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); reading.deleteDocument(doc.id); }}
                    className="p-2 text-stone-400 hover:text-red-500 transition-colors"
                    title={t('reading.deleteDoc')}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Panel Selection */}
      {reading.selectedDocument && (
        <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-amber-500" />
            <h2 className="font-serif text-xl">{t('reading.selectPanel')}</h2>
          </div>

          {library?.panels?.length > 0 ? (
            <select
              value={reading.selectedPanelId}
              onChange={e => reading.setSelectedPanelId(e.target.value)}
              className="input w-full"
            >
              <option value="">{t('reading.panelPlaceholder')}</option>
              {library.panels.map(panel => (
                <option key={panel.id} value={panel.id}>{panel.name}</option>
              ))}
            </select>
          ) : (
            <p className="text-stone-500 text-sm">{t('reading.noPanels')}</p>
          )}

          {reading.selectedPanelId && (
            <div className="mt-6">
              <button
                onClick={handleStartReading}
                disabled={!canStartReading}
                className="btn btn-primary w-full flex items-center justify-center gap-2 text-lg py-3"
              >
                <BookOpen className="w-5 h-5" />
                {t('reading.startReading')}
              </button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}


// ─── Reading Progress Stage ──────────────────────────────────

export function ReadingProgress({ reading }) {
  const { t } = useTranslation();
  const status = reading.readingStatus;

  if (!status) {
    return (
      <div className="text-center py-20">
        <Loader2 className="w-12 h-12 text-amber-500 animate-spin mx-auto mb-4" />
        <p className="text-stone-600">{t('reading.starting')}</p>
      </div>
    );
  }

  const total = status.personas_total || 0;
  const complete = status.personas_complete || 0;
  const failed = status.personas_failed || 0;
  const progress = total > 0 ? Math.round((complete / total) * 100) : 0;
  const phase = status.phase || status.status;

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <div className="text-center">
        <h1 className="font-serif text-3xl mb-2">{t('reading.readingTitle')}</h1>
        <p className="text-stone-500">{t('reading.readingSubtitle')}</p>
      </div>

      <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
        {/* Phase indicator */}
        <div className="flex items-center gap-3 mb-6">
          <Loader2 className="w-5 h-5 text-amber-500 animate-spin" />
          <span className="text-stone-700 font-medium capitalize">
            {phase === 'extracting' ? t('reading.phaseExtracting') :
             phase === 'reading' ? t('reading.phaseReading') :
             phase === 'complete' ? t('reading.phaseComplete') :
             t('reading.phasePending')}
          </span>
        </div>

        {/* Progress bar */}
        {phase === 'reading' && (
          <>
            <div className="w-full bg-stone-100 rounded-full h-3 mb-4">
              <div
                className="bg-amber-500 h-3 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-sm text-stone-500 mb-6">
              {complete}/{total} {t('reading.personasComplete')}
              {failed > 0 && <span className="text-red-500 ml-2">({failed} {t('reading.failed')})</span>}
            </p>
          </>
        )}

        {/* Per-persona status */}
        {status.persona_statuses?.length > 0 && (
          <div className="space-y-2">
            {status.persona_statuses.map((ps, i) => (
              <div key={ps.id || i} className="flex items-center justify-between py-2 px-3 rounded-lg bg-stone-50">
                <span className="text-sm font-medium text-stone-700">{ps.name || `Persona ${i+1}`}</span>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  ps.status === 'complete' ? 'bg-green-100 text-green-700' :
                  ps.status === 'processing' ? 'bg-amber-100 text-amber-700' :
                  ps.status === 'failed' ? 'bg-red-100 text-red-700' :
                  'bg-stone-100 text-stone-500'
                }`}>
                  {ps.status === 'complete' && <Check className="w-3 h-3 inline mr-1" />}
                  {ps.status === 'processing' && <Loader2 className="w-3 h-3 inline mr-1 animate-spin" />}
                  {ps.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      {reading.readingError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
          <AlertTriangle className="w-5 h-5 inline mr-2" />
          {reading.readingError}
        </div>
      )}
    </div>
  );
}


// ─── Review Stage ────────────────────────────────────────────

export function ReadingReview({ reading, onLaunch }) {
  const { t } = useTranslation();
  const [expandedPersona, setExpandedPersona] = useState(null);
  const results = reading.readingResults;

  if (!results) return null;

  const readings = results.readings || [];
  const completedReadings = readings.filter(r => r.status === 'complete');

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h1 className="font-serif text-3xl mb-2">{t('reading.reviewTitle')}</h1>
        <p className="text-stone-500">
          {t('reading.reviewSubtitle', { title: results.document_title })}
        </p>
      </div>

      {/* Moderator Synthesis */}
      {results.moderator_synthesis && (
        <section className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-5 h-5 text-amber-600" />
            <h2 className="font-serif text-lg text-amber-900">{t('reading.moderatorSynthesis')}</h2>
          </div>
          <p className="text-amber-900 text-sm whitespace-pre-wrap leading-relaxed">
            {results.moderator_synthesis}
          </p>
        </section>
      )}

      {/* Persona Readings */}
      <section className="space-y-4">
        {completedReadings.map(r => {
          const isExpanded = expandedPersona === r.persona_id;
          const notes = r.reading_notes || [];
          const noteTypes = {};
          notes.forEach(n => {
            noteTypes[n.type] = (noteTypes[n.type] || 0) + 1;
          });

          return (
            <div key={r.id} className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden">
              {/* Header */}
              <button
                onClick={() => setExpandedPersona(isExpanded ? null : r.persona_id)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-stone-50 transition-colors"
              >
                <div>
                  <h3 className="font-medium text-stone-800">{r.persona_name || r.persona_id}</h3>
                  <p className="text-sm text-stone-500 mt-1 line-clamp-2">{r.summary}</p>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {Object.entries(noteTypes).map(([type, count]) => (
                      <span key={type} className={`text-xs px-2 py-0.5 rounded-full ${
                        type === 'agreement' ? 'bg-green-100 text-green-700' :
                        type === 'objection' ? 'bg-red-100 text-red-700' :
                        type === 'question' ? 'bg-blue-100 text-blue-700' :
                        type === 'concern' ? 'bg-orange-100 text-orange-700' :
                        type === 'insight' ? 'bg-purple-100 text-purple-700' :
                        'bg-stone-100 text-stone-600'
                      }`}>
                        {type} ({count})
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); reading.rereadPersona(r.persona_id); }}
                    className="p-2 text-stone-400 hover:text-amber-600 transition-colors"
                    title={t('reading.reread')}
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                  {isExpanded ? <ChevronUp className="w-5 h-5 text-stone-400" /> : <ChevronDown className="w-5 h-5 text-stone-400" />}
                </div>
              </button>

              {/* Expanded Notes */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6 space-y-3 border-t border-stone-100 pt-4">
                      {/* Summary */}
                      <div className="p-4 bg-stone-50 rounded-xl">
                        <h4 className="text-sm font-medium text-stone-600 mb-2">{t('reading.overallTake')}</h4>
                        <p className="text-stone-800 text-sm">{r.summary}</p>
                      </div>

                      {/* Notes */}
                      {notes.map((note, i) => (
                        <div key={note.id || i} className={`p-3 rounded-lg border-l-3 ${
                          note.type === 'agreement' ? 'bg-green-50 border-l-green-400' :
                          note.type === 'objection' ? 'bg-red-50 border-l-red-400' :
                          note.type === 'question' ? 'bg-blue-50 border-l-blue-400' :
                          note.type === 'concern' ? 'bg-orange-50 border-l-orange-400' :
                          note.type === 'insight' ? 'bg-purple-50 border-l-purple-400' :
                          'bg-stone-50 border-l-stone-300'
                        }`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                              {note.type}
                            </span>
                            {note.intensity && (
                              <span className="text-xs text-stone-400">
                                {'●'.repeat(note.intensity)}{'○'.repeat(5 - note.intensity)}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-stone-800">{note.content}</p>
                          {note.quote && (
                            <p className="text-xs text-stone-500 mt-1 italic">&ldquo;{note.quote}&rdquo;</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </section>

      {/* Failed readings */}
      {readings.filter(r => r.status === 'failed').length > 0 && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
          <p className="text-red-700 text-sm">
            <AlertTriangle className="w-4 h-4 inline mr-1" />
            {readings.filter(r => r.status === 'failed').length} {t('reading.failedReadings')}
          </p>
        </div>
      )}

      {/* Launch Button */}
      <div className="text-center">
        <button
          onClick={onLaunch}
          className="btn btn-primary text-lg px-8 py-3 flex items-center gap-2 mx-auto"
        >
          <Play className="w-5 h-5" />
          {t('reading.launchDiscussion')}
        </button>
      </div>
    </div>
  );
}


// ─── Launch Config Stage ─────────────────────────────────────

export function ReadingLaunchConfig({ reading, config, updateConfig, onStart }) {
  const { t } = useTranslation();
  const [showModeratorSettings, setShowModeratorSettings] = useState(false);
  const [questions, setQuestions] = useState(['']);

  const docTitle = reading.readingResults?.document_title || '';

  const handleStart = () => {
    const launchConfig = {
      title: config.topic ? `${t('reading.discussionPrefix')}: ${config.topic}` : `${t('reading.discussionPrefix')}: ${docTitle}`,
      topic: config.topic || docTitle,
      description: config.description || `${t('reading.discussionDesc')} "${docTitle}"`,
      questions: questions.filter(q => q.trim()),
      moderator_briefing: config.moderatorBriefing || '',
      moderator_direction: config.moderatorDirection || '',
      moderator_avoid: config.moderatorAvoid || '',
      moderator_model: config.moderatorModel || 'claude-sonnet-4.6',
      language: config.language || 'sv',
      rounds: Math.max(questions.filter(q => q.trim()).length, 3),
    };
    onStart(launchConfig);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      <div className="text-center">
        <h1 className="font-serif text-3xl mb-2">{t('reading.launchTitle')}</h1>
        <p className="text-stone-500">{t('reading.launchSubtitle', { title: docTitle })}</p>
      </div>

      {/* Topic */}
      <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
        <label className="block text-sm font-medium text-stone-700 mb-2">{t('reading.topicLabel')}</label>
        <input
          type="text"
          value={config.topic || ''}
          onChange={e => updateConfig('topic', e.target.value)}
          className="input w-full"
          placeholder={docTitle}
        />
      </section>

      {/* Questions */}
      <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
        <h2 className="font-serif text-xl mb-4">{t('reading.questionsLabel')}</h2>
        <div className="space-y-3">
          {questions.map((q, i) => (
            <div key={i} className="flex gap-2">
              <span className="text-stone-400 mt-2 text-sm w-6">{i+1}.</span>
              <input
                type="text"
                value={q}
                onChange={e => {
                  const updated = [...questions];
                  updated[i] = e.target.value;
                  setQuestions(updated);
                }}
                className="input flex-1"
                placeholder={t('reading.questionPlaceholder')}
              />
              {questions.length > 1 && (
                <button
                  onClick={() => setQuestions(questions.filter((_, j) => j !== i))}
                  className="p-2 text-stone-400 hover:text-red-500"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
          <button
            onClick={() => setQuestions([...questions, ''])}
            className="text-amber-600 hover:text-amber-700 text-sm font-medium"
          >
            + {t('reading.addQuestion')}
          </button>
        </div>
      </section>

      {/* Moderator settings */}
      <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
        <button
          onClick={() => setShowModeratorSettings(!showModeratorSettings)}
          className="flex items-center gap-2 text-stone-700 font-medium"
        >
          {showModeratorSettings ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          {t('reading.moderatorSettings')}
        </button>

        {showModeratorSettings && (
          <div className="mt-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">{t('reading.briefingLabel')}</label>
              <textarea
                value={config.moderatorBriefing || ''}
                onChange={e => updateConfig('moderatorBriefing', e.target.value)}
                className="input w-full h-24 resize-y"
                placeholder={t('reading.briefingPlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">{t('reading.directionLabel')}</label>
              <textarea
                value={config.moderatorDirection || ''}
                onChange={e => updateConfig('moderatorDirection', e.target.value)}
                className="input w-full h-24 resize-y"
                placeholder={t('reading.directionPlaceholder')}
              />
            </div>
          </div>
        )}
      </section>

      {/* Start */}
      <div className="text-center">
        <button
          onClick={handleStart}
          className="btn btn-primary text-lg px-8 py-3 flex items-center gap-2 mx-auto"
        >
          <Play className="w-5 h-5" />
          {t('reading.startDiscussion')}
        </button>
      </div>
    </div>
  );
}
